import { useCallback, useEffect, useMemo, useState } from 'react'

import { useAllCarsQuery, CarNode } from '@/apollo/graphql'
import { IOption, IPageInfo, Maybe } from '@/types'
import { useApp } from '@/hooks'
import { carDisplayLabel, getFetchMoreStatus } from '@/utils'

interface IUseSelect {
  initial: Maybe<Partial<CarNode>>
}

const pageSize = 15

const initialPageInfo: IPageInfo = {
  hasNextPage: false,
  offset: 0,
  fetchMoreStatus: 'loading',
}

export const useCarSelect = ({ initial }: IUseSelect) => {
  const { isRTL } = useApp()

  const [cars, setCars] = useState<Partial<CarNode>[]>([])
  const [pageInfo, setPageInfo] = useState<IPageInfo>(initialPageInfo)
  const [search, setSearch] = useState<string>('')

  const { loading: carsLoading, networkStatus: carsNetworkStatus } =
    useAllCarsQuery({
      fetchPolicy: 'network-only',
      variables: {
        offset: pageInfo?.offset || 0,
        first: pageSize,
        number: search,
      },
      onCompleted: res => {
        if (res.allCars?.edges) {
          const _cars: Partial<CarNode>[] = []
          res.allCars.edges.forEach(e => {
            // @ts-ignore
            if (e?.node) _cars.push(e.node)
          })
          setCars(prev => [...prev, ..._cars])
          setPageInfo(prev => ({
            ...prev,
            hasNextPage: res.allCars?.pageInfo.hasNextPage,
          }))
        }
      },
    })

  const carOptions: IOption<string>[] = useMemo(() => {
    let exists = false
    const opts = cars.map(c => {
      if (!exists && initial?.id === c.id) {
        exists = true
      }
      return {
        label: carDisplayLabel(c, isRTL),
        value: c.id || '',
      }
    })

    if (!exists && initial && !search) {
      opts.unshift({
        label: carDisplayLabel(initial, isRTL),
        value: initial.id || '',
      })
    }

    return opts
  }, [cars, initial, isRTL, search])

  useEffect(() => {
    setPageInfo(prev => ({
      ...prev,
      fetchMoreStatus: getFetchMoreStatus({
        networkStatus: carsNetworkStatus,
        hasNextPage: prev?.hasNextPage,
        withForce: true,
      }),
    }))
  }, [carsNetworkStatus])

  const onFetchMore = useCallback(() => {
    setPageInfo(prev => ({ ...prev, offset: cars.length }))
  }, [cars.length])

  const onSearch = useCallback(
    (text: string) => {
      if (search !== text) {
        setCars([])
      }
      setSearch(text)
      setPageInfo(initialPageInfo)
    },
    [search],
  )

  return {
    carOptions,
    carsLoading,
    pageInfo,
    onFetchMore,
    onSearch,
  }
}
